import { useRef, useState } from 'react';
import { clsx } from 'clsx';
import { FileText, ImagePlus, Scan, Trash2, Download } from 'lucide-react';
import { useToast } from '@/shared/ui/Toast';
import {
  attachmentContentType,
  uploadToSignedUrl,
  type AttachmentUploadInput,
  type AttachmentUploadTicket,
  type ReferralAttachment,
} from '../api/referralsApi';
import styles from './AttachmentDropzone.module.css';

interface AttachmentDropzoneProps {
  attachments: ReferralAttachment[];
  /** Mint a signed upload URL for one file. */
  requestUpload?: (input: AttachmentUploadInput) => Promise<AttachmentUploadTicket>;
  /** Tell the API the bytes landed. */
  confirmUpload?: (attachmentId: string) => Promise<ReferralAttachment>;
  onDelete?: (attachmentId: string) => Promise<unknown>;
  onChanged?: () => void;
  disabled?: boolean;
  disabledReason?: string | null;
  /** Hide files from the other side (e.g. the sender's view of recipient uploads). */
  filterSide?: 'sender' | 'recipient';
  compact?: boolean;
}

const MAX_FILES_PER_DROP = 10;

/**
 * "Browse or drop files" — X-rays, DICOM, JPG, PDF. Uploads go straight to object storage through a
 * signed URL, so a 10 MB panoramic never passes through the API.
 */
export function AttachmentDropzone({
  attachments,
  requestUpload,
  confirmUpload,
  onDelete,
  onChanged,
  disabled = false,
  disabledReason,
  filterSide,
  compact = false,
}: AttachmentDropzoneProps) {
  const toast = useToast();
  const inputRef = useRef<HTMLInputElement>(null);
  const [dragging, setDragging] = useState(false);
  const [uploading, setUploading] = useState<string[]>([]);
  const canUpload = Boolean(requestUpload && confirmUpload) && !disabled;
  const visible = filterSide ? attachments.filter((a) => a.side === filterSide) : attachments;

  const handleFiles = async (files: FileList | File[]) => {
    if (!requestUpload || !confirmUpload) return;
    const list = Array.from(files).slice(0, MAX_FILES_PER_DROP);
    for (const file of list) {
      setUploading((u) => [...u, file.name]);
      try {
        const ticket = await requestUpload({
          filename: file.name,
          contentType: attachmentContentType(file),
          sizeBytes: file.size,
        });
        await uploadToSignedUrl(ticket.uploadUrl, file);
        await confirmUpload(ticket.attachment.id);
      } catch (err) {
        const message = (err as { response?: { data?: { error?: { message?: string } } } })?.response?.data?.error?.message;
        toast.error(message ?? `Could not upload ${file.name}`);
      } finally {
        setUploading((u) => u.filter((n) => n !== file.name));
      }
    }
    onChanged?.();
  };

  return (
    <div className={clsx(styles.wrap, compact && styles.compact)}>
      {canUpload && (
        <div
          className={clsx(styles.drop, dragging && styles.dropActive)}
          onDragOver={(e) => {
            e.preventDefault();
            setDragging(true);
          }}
          onDragLeave={() => setDragging(false)}
          onDrop={(e) => {
            e.preventDefault();
            setDragging(false);
            if (e.dataTransfer.files.length) void handleFiles(e.dataTransfer.files);
          }}
        >
          <ImagePlus size={28} className={styles.dropIcon} aria-hidden />
          <p className={styles.dropTitle}>
            <button type="button" className={styles.browse} onClick={() => inputRef.current?.click()}>
              Browse
            </button>{' '}
            or drop files
          </p>
          <p className={styles.dropHint}>X-Rays, DICOM, JPG, PDF and more</p>
          <input
            ref={inputRef}
            type="file"
            multiple
            hidden
            accept="image/*,.pdf,.dcm,.dicom,application/pdf,application/dicom"
            onChange={(e) => {
              if (e.target.files?.length) void handleFiles(e.target.files);
              e.target.value = '';
            }}
          />
        </div>
      )}
      {!canUpload && disabledReason && <p className={styles.disabledNote}>{disabledReason}</p>}
      {(visible.length > 0 || uploading.length > 0) && (
        <div className={styles.grid}>
          {visible.map((a) => (
            <AttachmentTile key={a.id} attachment={a} onDelete={onDelete} canDelete={!disabled && Boolean(onDelete)} />
          ))}
          {uploading.map((name) => (
            <div key={name} className={clsx(styles.tile, styles.tileUploading)}>
              <div className={styles.thumbIcon}>
                <ImagePlus size={22} />
              </div>
              <div className={styles.tileName}>{name}</div>
              <div className={styles.tileMeta}>Uploading…</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function AttachmentTile({
  attachment: a,
  onDelete,
  canDelete,
}: {
  attachment: ReferralAttachment;
  onDelete?: (id: string) => Promise<unknown>;
  canDelete: boolean;
}) {
  const [busy, setBusy] = useState(false);
  const isImage = a.mimeType.startsWith('image/') && a.mimeType !== 'image/tiff';
  const Icon = a.kind === 'xray' ? Scan : FileText;
  return (
    <div className={styles.tile}>
      {a.label && <span className={clsx(styles.kind, a.kind === 'xray' && styles.kindXray)}>{a.label}</span>}
      {isImage && a.url ? (
        <a href={a.url} target="_blank" rel="noreferrer" className={styles.thumbLink}>
          <img src={a.url} alt={a.originalFilename} className={styles.thumb} loading="lazy" />
        </a>
      ) : (
        <div className={styles.thumbIcon}>
          <Icon size={24} />
        </div>
      )}
      <div className={styles.tileName} title={a.originalFilename}>
        {a.originalFilename}
      </div>
      <div className={styles.tileFooter}>
        <span className={styles.tileMeta}>{formatBytes(a.sizeBytes)}</span>
        <span className={styles.tileActions}>
          {a.url && (
            <a href={a.url} target="_blank" rel="noreferrer" className={styles.iconBtn} aria-label="Download">
              <Download size={14} />
            </a>
          )}
          {canDelete && onDelete && (
            <button
              type="button"
              className={clsx(styles.iconBtn, styles.iconDanger)}
              aria-label="Remove file"
              disabled={busy}
              onClick={async () => {
                setBusy(true);
                try {
                  await onDelete(a.id);
                } finally {
                  setBusy(false);
                }
              }}
            >
              <Trash2 size={14} />
            </button>
          )}
        </span>
      </div>
    </div>
  );
}

function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
}
